/**
 * URL extraction + classification for automation messages.
 *
 * Walks the rendered message content, finds bare URLs and markdown
 * links, and tags each with the third-party app slug (`posthog`,
 * `linear`, `gmail`, …) so the AutomationMessageCard footer can render
 * an action button with the matching app logo.
 *
 * Slug derivation mirrors `server/extract-apps-used.ts` and the inline
 * client mirror in `AutomationDetailEditTab.tsx` — strip well-known
 * subdomain prefixes (eu/us/app/api/www/cloud) then take the first
 * remaining label. So `eu.posthog.com` → `posthog`, `app.hubspot.com`
 * → `hubspot`, `mail.google.com` → `google`. Unknown hostnames return
 * `null` and the button falls back to the hostname as its label.
 *
 * Smart label heuristics turn long URLs into short verbs that explain
 * what the button *does*:
 *   - `…/dashboard/…`  → "Open dashboard"
 *   - `…/issue/…`      → "View issue"
 *   - `…/contact/…`    → "View contact"
 *   - `…/pull/N`       → "View pull request"
 *   - bare home page   → "Visit {AppName}"
 *
 * Pure module — no React imports — so it stays cheap to unit-test.
 */

const SUBDOMAIN_SKIP = new Set([
  "eu",
  "us",
  "app",
  "api",
  "www",
  "cloud",
  "mail",
]);

/** Capitalize the first letter of a slug for display. */
function titleCase(slug: string): string {
  return slug.length === 0 ? slug : slug[0].toUpperCase() + slug.slice(1);
}

/** Pull the slug out of a hostname using the same rules as the server. */
export function slugFromHostname(hostname: string): string | null {
  const labels = hostname.toLowerCase().split(".");
  for (const label of labels) {
    if (label.length === 0) continue;
    if (SUBDOMAIN_SKIP.has(label)) continue;
    return label;
  }
  return null;
}

export interface ActionUrl {
  /** Absolute URL to navigate to. */
  url: string;
  /** Hostname for fallback display. */
  hostname: string;
  /** Lowercase app slug derived from hostname, or null if unknown. */
  slug: string | null;
  /** Short verb label rendered on the button. */
  label: string;
  /** Optional caller-provided label (markdown link text) preserved verbatim. */
  customLabel?: string;
}

/**
 * Map URL pathname patterns to display verbs. Falls back to
 * "Visit {Slug}" when no pattern matches.
 */
function deriveLabel(url: URL, slug: string | null): string {
  const path = url.pathname.toLowerCase();
  // Generic patterns first — work across most SaaS products.
  if (/(^|\/)dashboards?(\/|$)/.test(path)) return "Open dashboard";
  if (/(^|\/)issues?(\/|$)/.test(path)) return "View issue";
  if (/(^|\/)pulls?(\/|$)/.test(path) || /(^|\/)pr(\/|$)/.test(path)) {
    return "View pull request";
  }
  if (/(^|\/)contacts?(\/|$)/.test(path)) return "View contact";
  if (/(^|\/)deals?(\/|$)/.test(path)) return "View deal";
  if (/(^|\/)tasks?(\/|$)/.test(path)) return "View task";
  if (/(^|\/)tickets?(\/|$)/.test(path)) return "View ticket";
  if (/(^|\/)docs?(\/|$)/.test(path)) return "Open doc";
  if (/(^|\/)reports?(\/|$)/.test(path)) return "View report";
  if (/(^|\/)insights?(\/|$)/.test(path)) return "Open insight";
  if (/(^|\/)events?(\/|$)/.test(path)) return "View event";
  if (/(^|\/)(mail|messages?)(\/|$)/.test(path)) return "Open mail";
  if (/(^|\/)projects?(\/|$)/.test(path)) return "Open project";
  // Fallback: "Visit AppName" if we know the app, else hostname.
  return slug ? `Visit ${titleCase(slug)}` : `Visit ${url.hostname}`;
}

/** Bare-URL regex (mirrors `markdown-renderer.tsx`'s URL_REGEX). */
const BARE_URL_REGEX =
  /(?:https?:\/\/[^\s<>")\]]+|(?<![/@\w.-])(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(?:\/[^\s<>")\]]*)?)/gi;

/** Markdown link regex — captures `[label](url)`. */
const MD_LINK_REGEX = /\[([^\]\n]+)\]\(([^)\s]+)\)/g;

function stripMarkdownCode(content: string): string {
  return content
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/~~~[\s\S]*?~~~/g, " ")
    .replace(/`[^`\n]+`/g, " ");
}

function classifyUrl(rawUrl: string, customLabel?: string): ActionUrl | null {
  const cleaned = rawUrl.replace(/[.,;:!?)]+$/, "");
  // Reject non-http(s) schemes early — `mailto:`, `tel:`, `javascript:`,
  // `data:`, etc. shouldn't be turned into "Visit X" buttons. We also
  // reject anchor-only hrefs (`#foo`) and relative paths (`/foo`) that
  // appear inside markdown-link parens.
  const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(cleaned);
  if (hasScheme && !/^https?:\/\//i.test(cleaned)) return null;
  if (cleaned.startsWith("#") || cleaned.startsWith("/")) return null;
  const absolute = hasScheme ? cleaned : `https://${cleaned}`;
  let parsed: URL;
  try {
    parsed = new URL(absolute);
  } catch {
    return null;
  }
  // `new URL("https://")` succeeds with an empty hostname; reject those.
  const hostname = parsed.hostname.toLowerCase();
  if (!hostname) return null;
  const slug = slugFromHostname(hostname);
  return {
    url: absolute,
    hostname,
    slug,
    label: customLabel ?? deriveLabel(parsed, slug),
    customLabel,
  };
}

/**
 * Walk message content and return one classified entry per unique URL.
 * Markdown links keep their author-provided label as `customLabel`,
 * which the renderer prefers over the derived "Open dashboard" verb.
 *
 * Markdown links are extracted first; the bare-URL pass then skips any
 * URL already captured. This avoids double-counting `[Open](https://x)`
 * in a body that also has the bare `https://x` further down.
 */
export function extractActionUrls(content: string): ActionUrl[] {
  const seen = new Map<string, ActionUrl>();

  // Strip code first so dotted identifiers like `tx.mutate.foo` don't
  // get mistaken for schemeless URLs.
  const withoutCode = stripMarkdownCode(content);
  // Strip markdown image embeds so we don't lift them into action buttons.
  const stripped = withoutCode.replace(/!\[[^\]]*\]\([^)]*\)/g, "");

  // Pass 1: markdown links — preserve author label.
  for (const match of stripped.matchAll(MD_LINK_REGEX)) {
    const label = match[1]?.trim();
    const target = match[2];
    if (!target) continue;
    const entry = classifyUrl(target, label || undefined);
    if (!entry) continue;
    if (!seen.has(entry.url)) seen.set(entry.url, entry);
  }

  // Pass 2: bare URLs — skip the segments already consumed by md-link
  // parsing so we don't classify the same href twice with different
  // labels. The simplest blunt cut: drop md-link spans before re-scan.
  const withoutMdLinks = stripped.replace(MD_LINK_REGEX, " ");
  for (const match of withoutMdLinks.matchAll(BARE_URL_REGEX)) {
    const entry = classifyUrl(match[0]);
    if (!entry) continue;
    if (!seen.has(entry.url)) seen.set(entry.url, entry);
  }

  return [...seen.values()];
}
